// Displays the single AccessUser object

import React, { Component } from 'react';
import { bool, func, string, number } from 'prop-types';
import Select from 'react-select';
import MuiThemeProvider from 'material-ui/styles/MuiThemeProvider';
import getMuiTheme from "material-ui/styles/getMuiTheme";
import DatePicker from 'material-ui/DatePicker';
import moment from 'moment';

import UserBadge from '../../../../UserBadge';
import portalTheme from "../../../../../themes/portalTheme";

import cx from 'classnames';
import s from './AccessManagementUser.css';


export default class AccessManagementUser extends Component {
  static propTypes = {
    active: bool,
    closeConfirmDialog: func,
    closeAlertDialog: func,
    deleteUser: func,
    disableUser: func,
    enableUser: func,
    expiration: number,
    expired: bool,
    firstName: string,
    id: number,
    lastName: string,
    openConfirmDialog: func,
    openErrorDialog: func,
    openSuccessDialog: func,
    reason: string,
    removeExpiration: func,
    updateExpiration: func,
  };

  constructor(props) {
    super(props);

    this.state = {
      actionValue: null,
    };
  }

  handleDeleteUser = () => {
    this.props.deleteUser(
      this.props.id,
      this.deleteUserSuccessCallback,
      this.errorCallback,
    );

    // Return true so AuthLayout will close the confirmDialog
    return true;
  };

  handleDisableUser = () => {
    this.props.disableUser(
      this.props.id,
      this.disableUserSuccessCallback,
      this.errorCallback,
    );

    // Return true so AuthLayout will close the confirmDialog
    return true;
  };

  handleEnableUser = () => {
    this.props.enableUser(
      this.props.id,
      this.enableUserSuccessCallback,
      this.errorCallback,
    );

    // Return true so AuthLayout will close the confirmDialog
    return true;
  };

  // Success and Error callbacks are handled in familyAccessActions
  handleRemoveExpiration = () => {
    this.props.removeExpiration(this.props.id);

    // Return true so AuthLayout will close the confirmDialog
    return true;
  };

  handleUpdateExpiration = (alwaysNullArg, date) => {
    if (!date) {
      return;
    }

    const data = {
      id: this.props.id,
      expiration: date,
    };

    this.props.updateExpiration(data);

    // Return true so AuthLayout will close the confirmDialog
    return true;
  };

  deleteUserSuccessCallback = () =>
    this.props.openSuccessDialog({
      title: `${this.props.firstName} ${this.props.lastName} was deleted.`,
      okCallback: this.handleAlertDialogClose,
    });

  disableUserSuccessCallback = () =>
    this.props.openSuccessDialog({
      title: `${this.props.firstName} ${this.props.lastName} disabled.`,
      okCallback: this.handleAlertDialogClose,
    });

  enableUserSuccessCallback = () =>
    this.props.openSuccessDialog({
      title: `${this.props.firstName} ${this.props.lastName} enabled.`,
      okCallback: this.handleAlertDialogClose,
    });

  errorCallback = response =>
    this.props.openErrorDialog({
      title: response.message.title,
      content: response.message.body,
      okCallback: this.handleAlertDialogClose,
    });

  handleConfirmDelete = () =>
    this.props.openConfirmDialog({
      title: 'Confirm Delete',
      content: 'Are you sure you want to permanently delete this user?',
      cancelCallback: this.handleConfirmDialogCancel,
      okCallback: this.handleDeleteUser,
    });

  handleConfirmDisable = () =>
    this.props.openConfirmDialog({
      title: 'Confirm Disable',
      content: 'Are you sure you want to disable this user?',
      cancelCallback: this.handleConfirmDialogCancel,
      okCallback: this.handleDisableUser,
    });

  handleConfirmEnable = () =>
    this.props.openConfirmDialog({
      title: 'Confirm Enable',
      content: 'Are you sure you want to enable this user?',
      cancelCallback: this.handleConfirmDialogCancel,
      okCallback: this.handleEnableUser,
    });

  handleConfirmRemoveExpiration = () =>
    this.props.openConfirmDialog({
      title: 'Confirm Remove Expiration Date',
      content: 'Are you sure you want to remove the expiration date for this user?',
      cancelCallback: this.handleConfirmDialogCancel,
      okCallback: this.handleRemoveExpiration,
    });

  // Call the APIs based on the action value passed
  handleActionChange = (action) => {
    switch (action.value) {
      case 'Delete':
        this.setState(prevState => ({ actionValue: action.value }));
        this.handleConfirmDelete();
        break;
      case 'Disable':
        this.setState(prevState => ({ actionValue: action.value }));
        this.handleConfirmDisable();
        break;
      case 'Enable':
        this.setState(prevState => ({ actionValue: action.value }));
        this.handleConfirmEnable();
        break;
      case 'Remove Expiration':
        this.setState(prevState => ({ actionValue: action.value }));
        this.handleConfirmRemoveExpiration();
        break;
      default:

    }
  };

  handleConfirmDialogCancel = () => {
    this.props.closeConfirmDialog();

    // Clear the selected action when user 'cancels' the confirm
    this.setState({
      actionValue: null,
    });
  };

  // Close the alert dialog, refresh User list
  // Uses a callback to the parent RootContainer to update AccessUserList state
  handleAlertDialogClose = () => {
    this.props.closeAlertDialog();

    // Callback to parent FamilyAccessContainer to refresh User list
    this.props.handleRefreshUsers();
  };

  // We hide the datepicker initially, and then call it this way
  handleOpenDatePicker = () => this.refs.datePicker.openDialog();

  renderCheckMark = () =>
    <svg className={s.checkmark} x="0px" y="0px" viewBox="0 0 26.9 20.4">
      <path d="M8.3,19.8l-7.5-7.5c-0.8-0.8-0.8-2,0-2.8l0,0c0.8-0.8,2-0.8,2.8,0L9.1,15L23.3,0.8c0.8-0.8,2-0.8,2.8,0l0,0c0.8,0.8,0.8,2,0,2.8L10,19.8C9.5,20.3,8.8,20.3,8.3,19.8z"/>
    </svg>;

  render = () => {
    const {
      active,
      reason,
      expiration,
      expired,
      firstName,
      lastName,
    } = this.props;

    // If the user's not expired and is active, display 'enabled', else 'disabled'
    const enabled = !expired && active;

    // If there's an expiration date, display it, otherwise 'no expiration'
    const renderExpiration = (expiration ? `${moment(expiration).format('MMM Do, YYYY')}` : '(No Expiration)');
    // disabledUserStyle applied when user is disabled
    const disabledUserStyle = cx({[s.disabled]: !enabled})

    // Renders react-select. This component is always expecting an array of objects with label and value as keys.
    const renderActionsDropdown = () => {

      const disable = { label: "Disable", value: "Disable" };
      const enable = { label: "Enable", value: "Enable" };
      const removeExpiration = { label: "Remove Expiration", value: "Remove Expiration" };
      const deleteUser = { label: "Delete", value: "Delete" };

      const buildFields = () => {
        let fields = [];

        if (!expired && active) {
          fields.push(disable);
        } else {
          fields.push(enable);
        }

        if (expiration) {
          fields.push(removeExpiration);
        }

        fields.push(deleteUser);

        return fields;
      };

      return (
        <Select
          searchable={false}
          clearable={false}
          id={`${this.props.id}_actions`}
          name={`${this.props.id}_actions`}
          className="familyAccess"
          optionClassName="familyAccess__option"
          placeholder="Select an action"
          options={buildFields()}
          value={this.state.actionValue}
          onChange={this.handleActionChange}
          inputProps={{title:'Family Access Actions', 'aria-label': 'Family Access Actions'}}
        />
      );

    };

    return (
      <section className={s.component}>
        <div className={s.user}>
          <div className={s.flexWrapper}>
            <UserBadge
              className={cx(s.userBadge, disabledUserStyle)}
              firstInitial={this.props.firstName.charAt(0)}
              lastInitial={this.props.lastName.charAt(0)}
            />
            <div className={cx(s.flexColumn, s.userDetails)}>
              <p className={disabledUserStyle}>
                {firstName} {lastName}
              </p>
              <p className={cx(s.expirationDate, disabledUserStyle)}>
                {renderExpiration}
              </p>
              {reason &&
                // If there's a reason display it
                <p className={s.reason}>
                  {reason}
                </p>
              }
            </div>
          </div>
          <div className={s.flexWrapper}>
            <div className={s.actions}>
              <div className={s.dropdownWrapper}>
                {enabled &&
                  this.renderCheckMark()
                }
                {renderActionsDropdown()}
              </div>
              <button
                name="Update Expiration"
                className={s.btn}
                onClick={this.handleOpenDatePicker}
                type="button"
              >
                Update Expiration
              </button>
            </div>
          </div>
        </div>

        {
          // hidden datepicker is triggered by handleUpdateExpiration
        }
        <MuiThemeProvider muiTheme={getMuiTheme(portalTheme)}>
          <DatePicker
            ref="datePicker"
            className={s.hidden}
            onChange={this.handleUpdateExpiration}
            defaultDate={moment().add(1, 'days').toDate()}
            minDate={moment().add(1, 'days').toDate()}
            floatingLabelText="Ranged Date Picker"
          />
        </MuiThemeProvider>
      </section>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/Pages/Account/AccessManagement/AccessManagementUser/AccessManagementUser.js